import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  FileInput, Plus, Loader2, Trash2, Code2, Inbox, ExternalLink, Copy, X, GripVertical, Eye, TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIFECYCLE = ['subscriber', 'lead', 'mql', 'sql', 'opportunity', 'customer'];

const STANDARD_FIELDS = [
  { key: 'full_name', label: 'Name', type: 'text' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'phone' },
  { key: 'company', label: 'Company', type: 'text' },
  { key: 'job_title', label: 'Job title', type: 'text' },
  { key: 'message', label: 'Message', type: 'textarea' },
  { key: 'consent', label: 'Consent', type: 'consent' },
];

const emptyForm = () => ({
  name: '',
  fields: [
    { key: 'full_name', label: 'Name', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'email', required: true },
  ],
  submit_label: 'Submit',
  success_message: "Thanks - we'll be in touch shortly.",
  lifecycle_stage: 'lead',
  list_id: 'none',
  notify_email: '',
  consent_text: 'I agree to be contacted about my enquiry.',
  redirect_url: '',
});

const Forms = () => {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builder, setBuilder] = useState(null); // form object being edited/created
  const [submissionsFor, setSubmissionsFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);

  const fetchForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/forms`);
      setForms(res.data.forms || []);
    } catch { toast.error('Failed to load forms'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const handleDelete = async (f, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete form "${f.name}"?`)) return;
    try {
      await axios.delete(`${API}/forms/${f.id}`);
      toast.success('Form deleted');
      fetchForms();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="forms-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl text-white mb-2 flex items-center gap-3">
            <FileInput className="w-8 h-8 text-violet-400" />
            Web Forms
          </h1>
          <p className="text-slate-400">Capture leads from your website straight into Contacts.</p>
        </div>
        <Button className="bg-violet-600 hover:bg-violet-500" onClick={() => setBuilder(emptyForm())} data-testid="new-form-btn">
          <Plus className="w-4 h-4 mr-2" /> New Form
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20" data-testid="forms-loading"><Loader2 className="w-8 h-8 animate-spin text-slate-500" /></div>
      ) : forms.length === 0 ? (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="text-center py-16 text-slate-500">No forms yet. Click "New Form" to build your first lead-capture form.</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {forms.map(f => (
            <div key={f.id} className="bg-slate-900/60 border border-slate-800 rounded-lg p-4 space-y-3" data-testid={`form-card-${f.id}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm text-white font-medium">{f.name}</p>
                <button onClick={(e) => handleDelete(f, e)} className="text-slate-600 hover:text-rose-400" data-testid={`delete-form-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-slate-500" data-testid={`form-stats-${f.id}`}>
                <span className="flex items-center gap-1" title="Form views"><Eye className="w-3 h-3" /> {f.view_count || 0} views</span>
                <span className="flex items-center gap-1" title="Submissions"><Inbox className="w-3 h-3" /> {f.submission_count || 0} subs</span>
                <span className="flex items-center gap-1 text-emerald-400" title="Conversion rate (submissions ÷ views)">
                  <TrendingUp className="w-3 h-3" /> {f.view_count ? Math.round((f.submission_count || 0) / f.view_count * 100) : 0}%
                </span>
                <span>{(f.fields || []).length} fields</span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-7 text-xs" onClick={() => setBuilder({ ...f, list_id: f.list_id || 'none', lifecycle_stage: f.lifecycle_stage || 'none' })} data-testid={`edit-form-${f.id}`}>Edit</Button>
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-7 text-xs" onClick={() => setShareFor(f)} data-testid={`share-form-${f.id}`}><Code2 className="w-3 h-3 mr-1" /> Embed</Button>
                <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700 h-7 text-xs" onClick={() => setSubmissionsFor(f)} data-testid={`submissions-form-${f.id}`}><Inbox className="w-3 h-3 mr-1" /> Submissions</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {builder && <FormBuilder initial={builder} onClose={() => setBuilder(null)} onSaved={() => { setBuilder(null); fetchForms(); }} />}
      {submissionsFor && <SubmissionsDialog form={submissionsFor} onClose={() => setSubmissionsFor(null)} />}
      {shareFor && <ShareDialog form={shareFor} onClose={() => setShareFor(null)} />}
    </div>
  );
};

const FormBuilder = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState([]);
  const [customProps, setCustomProps] = useState([]);

  useEffect(() => {
    axios.get(`${API}/lists`, { params: { object_type: 'contact' } }).then(r => setLists((r.data.lists || []).filter(l => l.list_type === 'static'))).catch(() => {});
    axios.get(`${API}/properties`, { params: { object_type: 'contact' } }).then(r => setCustomProps(r.data.properties || [])).catch(() => {});
  }, []);

  const usedKeys = new Set(form.fields.map(f => f.key));

  const addField = (def) => setForm(f => ({ ...f, fields: [...f.fields, { ...def, required: false }] }));
  const removeField = (key) => setForm(f => ({ ...f, fields: f.fields.filter(x => x.key !== key) }));
  const updateField = (key, patch) => setForm(f => ({ ...f, fields: f.fields.map(x => x.key === key ? { ...x, ...patch } : x) }));

  const availableStandard = STANDARD_FIELDS.filter(s => !usedKeys.has(s.key));
  const availableCustom = customProps.filter(p => !usedKeys.has(`custom:${p.name}`));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Form name required'); return; }
    if (!form.fields.some(f => f.key === 'email')) { toast.error('Forms must include an Email field'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        fields: form.fields,
        submit_label: form.submit_label || 'Submit',
        success_message: form.success_message,
        lifecycle_stage: form.lifecycle_stage !== 'none' ? form.lifecycle_stage : null,
        list_id: form.list_id !== 'none' ? form.list_id : null,
        notify_email: form.notify_email || null,
        consent_text: form.consent_text || null,
        redirect_url: form.redirect_url || null,
      };
      if (form.id) await axios.patch(`${API}/forms/${form.id}`, payload);
      else await axios.post(`${API}/forms`, payload);
      toast.success(form.id ? 'Form updated' : 'Form created');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save form'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="form-builder">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">{form.id ? 'Edit Form' : 'New Form'}</DialogTitle>
          <DialogDescription className="text-slate-400">Pick fields, then decide what happens on submit.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div><Label className="text-slate-300">Form name *</Label>
            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-slate-950 border-slate-800" placeholder="e.g. Care demo request" data-testid="form-name-input" />
          </div>

          {/* Fields */}
          <div>
            <Label className="text-slate-300 mb-2 block">Fields</Label>
            <div className="space-y-2">
              {form.fields.map(f => (
                <div key={f.key} className="bg-slate-950 border border-slate-800 rounded-md p-2.5 flex items-center gap-2" data-testid={`builder-field-${f.key}`}>
                  <GripVertical className="w-4 h-4 text-slate-700 shrink-0" />
                  <Input value={f.label} onChange={e => updateField(f.key, { label: e.target.value })} className="bg-slate-900 border-slate-800 h-8 flex-1" />
                  <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-500 shrink-0">{f.type}</Badge>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[11px] text-slate-500">Required</span>
                    <Switch checked={!!f.required} onCheckedChange={v => updateField(f.key, { required: v })} disabled={f.key === 'email'} />
                  </div>
                  {f.key !== 'email' && (
                    <button onClick={() => removeField(f.key)} className="text-slate-600 hover:text-rose-400 shrink-0" data-testid={`remove-field-${f.key}`}><X className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
            {(availableStandard.length > 0 || availableCustom.length > 0) && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {availableStandard.map(s => (
                  <button key={s.key} onClick={() => addField(s)} className="text-xs px-2.5 py-1 rounded-full border border-slate-700 text-slate-400 hover:border-violet-600 hover:text-violet-300 transition-colors" data-testid={`add-field-${s.key}`}>
                    <Plus className="w-3 h-3 inline mr-0.5" /> {s.label}
                  </button>
                ))}
                {availableCustom.map(p => (
                  <button key={p.name} onClick={() => addField({ key: `custom:${p.name}`, label: p.label, type: ['select', 'multiselect'].includes(p.field_type) ? 'select' : (p.field_type === 'checkbox' ? 'checkbox' : (p.field_type === 'textarea' ? 'textarea' : 'text')), options: p.options })} className="text-xs px-2.5 py-1 rounded-full border border-violet-800/40 text-violet-300/80 hover:border-violet-600 transition-colors">
                    <Plus className="w-3 h-3 inline mr-0.5" /> {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* On submit */}
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 space-y-3">
            <p className="text-xs text-slate-500 uppercase tracking-wider">On submit</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label className="text-slate-400 text-xs">Lifecycle stage</Label>
                <Select value={form.lifecycle_stage} onValueChange={v => setForm({ ...form, lifecycle_stage: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-9" data-testid="form-lifecycle"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none">- none -</SelectItem>
                    {LIFECYCLE.map(s => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-slate-400 text-xs">Add to list</Label>
                <Select value={form.list_id} onValueChange={v => setForm({ ...form, list_id: v })}>
                  <SelectTrigger className="bg-slate-900 border-slate-800 h-9" data-testid="form-list"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="none">- none -</SelectItem>
                    {lists.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-slate-400 text-xs">Notify email (on each submission)</Label>
              <Input value={form.notify_email} onChange={e => setForm({ ...form, notify_email: e.target.value })} className="bg-slate-900 border-slate-800 h-9" placeholder="you@company.com" data-testid="form-notify-email" />
            </div>
          </div>

          {/* Messaging */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-slate-400 text-xs">Submit button label</Label>
              <Input value={form.submit_label} onChange={e => setForm({ ...form, submit_label: e.target.value })} className="bg-slate-950 border-slate-800 h-9" />
            </div>
            <div><Label className="text-slate-400 text-xs">Redirect URL (optional)</Label>
              <Input value={form.redirect_url} onChange={e => setForm({ ...form, redirect_url: e.target.value })} className="bg-slate-950 border-slate-800 h-9" placeholder="https://…/thank-you" />
            </div>
          </div>
          <div><Label className="text-slate-400 text-xs">Success message</Label>
            <Input value={form.success_message} onChange={e => setForm({ ...form, success_message: e.target.value })} className="bg-slate-950 border-slate-800 h-9" />
          </div>
          {form.fields.some(f => f.type === 'consent') && (
            <div><Label className="text-slate-400 text-xs">Consent text</Label>
              <Input value={form.consent_text} onChange={e => setForm({ ...form, consent_text: e.target.value })} className="bg-slate-950 border-slate-800 h-9" data-testid="form-consent-text" />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button className="bg-violet-600 hover:bg-violet-500" onClick={save} disabled={saving} data-testid="save-form-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : (form.id ? 'Save changes' : 'Create form')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SubmissionsDialog = ({ form, onClose }) => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/forms/${form.id}/submissions`)
      .then(r => setSubs(r.data.submissions || []))
      .catch(() => toast.error('Failed to load submissions'))
      .finally(() => setLoading(false));
  }, [form.id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">{form.name} - Submissions</DialogTitle>
          <DialogDescription className="text-slate-400">{form.submission_count || 0} total</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
        ) : subs.length === 0 ? (
          <p className="text-sm text-slate-600 italic py-8 text-center">No submissions yet.</p>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {subs.map(s => (
              <div key={s.id} className="bg-slate-950 border border-slate-800 rounded-md p-3" data-testid={`submission-${s.id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm text-white">{s.data?.full_name || s.data?.email || 'Submission'}</span>
                  <span className="text-[11px] text-slate-600">{new Date(s.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                  {Object.entries(s.data || {}).filter(([k]) => k !== 'full_name').map(([k, v]) => (
                    <span key={k}><span className="text-slate-600">{k.replace('custom:', '')}:</span> {String(v)}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ShareDialog = ({ form, onClose }) => {
  const hostedUrl = `${window.location.origin}/f/${form.id}`;
  const iframe = `<iframe src="${hostedUrl}" width="100%" height="640" frameborder="0" style="border:0;max-width:480px;"></iframe>`;
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white flex items-center gap-2"><Code2 className="w-5 h-5 text-violet-400" /> Embed "{form.name}"</DialogTitle>
          <DialogDescription className="text-slate-400">Share the hosted link or drop the embed code into your site.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Hosted link</Label>
            <div className="flex gap-2">
              <Input readOnly value={hostedUrl} className="bg-slate-950 border-slate-800 text-slate-300 text-xs" data-testid="hosted-url" />
              <Button size="sm" variant="outline" className="bg-slate-900 border-slate-700" onClick={() => copy(hostedUrl, 'Link')} data-testid="copy-link-btn"><Copy className="w-4 h-4" /></Button>
              <a href={hostedUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="bg-slate-900 border-slate-700"><ExternalLink className="w-4 h-4" /></Button></a>
            </div>
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">Embed code (iframe)</Label>
            <textarea readOnly value={iframe} rows={3} className="w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-xs text-slate-300 font-mono" data-testid="embed-code" />
            <Button size="sm" className="bg-violet-600 hover:bg-violet-500 mt-2" onClick={() => copy(iframe, 'Embed code')} data-testid="copy-embed-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy embed code</Button>
          </div>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Forms;
